import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiArrowUp, FiHelpCircle, FiRefreshCw } from 'react-icons/fi';
import { useI18n } from '../i18n/I18nProvider';

const MAX_CACHED_MESSAGES = 40;

const getChatStorageKey = () => {
  const fallbackKey = 'englishpractice:ai-help:v1:local-session';

  try {
    const token = localStorage.getItem('token');
    if (!token) return fallbackKey;

    const encodedPayload = token.split('.')[1];
    if (!encodedPayload) return fallbackKey;

    const normalizedPayload = encodedPayload
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(encodedPayload.length / 4) * 4, '=');
    const payload = JSON.parse(atob(normalizedPayload));
    const userId = String(payload.user_id || payload.sub || '').trim();
    const role = String(payload.role || 'user').trim();

    return userId
      ? `englishpractice:ai-help:v1:${role}:${userId}`
      : fallbackKey;
  } catch (error) {
    return fallbackKey;
  }
};

const readCachedMessages = (storageKey) => {
  try {
    const cached = JSON.parse(localStorage.getItem(storageKey) || '[]');
    if (!Array.isArray(cached)) return [];

    return cached
      .filter((message) => (
        message
        && (message.role === 'user' || message.role === 'assistant')
        && typeof message.content === 'string'
        && message.content.trim()
      ))
      .slice(-MAX_CACHED_MESSAGES)
      .map((message, index) => ({
        id: String(message.id || `cached-${index}`),
        role: message.role,
        content: message.content.slice(0, 3000),
        links: Array.isArray(message.links)
          ? message.links
            .filter((link) => (
              link
              && typeof link.label === 'string'
              && typeof link.path === 'string'
              && link.path.startsWith('/')
            ))
            .slice(0, 6)
          : [],
      }));
  } catch (error) {
    return [];
  }
};

const HelpManualPage = () => {
  const { t } = useI18n();
  const location = useLocation();
  const textareaRef = useRef(null);
  const askingRef = useRef(false);
  const chatStorageKey = useMemo(() => getChatStorageKey(), []);
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState(() => readCachedMessages(chatStorageKey));
  const [asking, setAsking] = useState(false);

  const isTeacher = location.pathname.startsWith('/admin/');
  const suggestions = useMemo(
    () => (
      isTeacher
        ? [
          t('How do I assign words to one student?'),
          t('Where can I see today’s student practice?'),
          t('How do assignment deadlines work?'),
        ]
        : [
          t('How do I start today’s assignment?'),
          t('Where can I review my words?'),
          t('What do the four practice steps mean?'),
        ]
    ),
    [isTeacher, t],
  );

  useEffect(() => {
    try {
      const cacheableMessages = messages
        .filter((message) => message.status !== 'error')
        .slice(-MAX_CACHED_MESSAGES)
        .map(({ id, role, content, links }) => ({
          id,
          role,
          content,
          ...(Array.isArray(links) && links.length ? { links } : {}),
        }));
      localStorage.setItem(chatStorageKey, JSON.stringify(cacheableMessages));
    } catch (error) {
      // Chat remains usable if storage is unavailable or full.
    }
  }, [chatStorageKey, messages]);

  const startNewChat = () => {
    try {
      localStorage.removeItem(chatStorageKey);
    } catch (error) {
      // Clearing the visible conversation is still useful without storage access.
    }
    setMessages([]);
    setQuestion('');
    textareaRef.current?.focus();
  };

  const askAssistant = async (event, suggestedQuestion = '') => {
    event?.preventDefault();
    const cleanQuestion = String(suggestedQuestion || question).trim();
    if (!cleanQuestion || askingRef.current) return;
    askingRef.current = true;

    const priorMessages = messages
      .filter((message) => message.status !== 'error')
      .slice(-24)
      .map(({ role, content }) => ({ role, content }));
    const userMessage = {
      id: `${Date.now()}-user`,
      role: 'user',
      content: cleanQuestion,
    };
    setMessages((current) => [...current, userMessage]);
    setQuestion('');
    setAsking(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/help/ask', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          question: cleanQuestion,
          history: priorMessages,
          current_path: location.pathname,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.message || 'The help assistant could not answer right now.');
      }
      setMessages((current) => [
        ...current,
        {
          id: `${Date.now()}-assistant`,
          role: 'assistant',
          content: data.answer || '',
          links: Array.isArray(data.links) ? data.links : [],
        },
      ]);
    } catch (error) {
      setMessages((current) => [
        ...current,
        {
          id: `${Date.now()}-error`,
          role: 'assistant',
          content: t(error.message || 'The help assistant could not answer right now.'),
          status: 'error',
        },
      ]);
    } finally {
      askingRef.current = false;
      setAsking(false);
      window.setTimeout(() => textareaRef.current?.focus(), 0);
    }
  };

  const handleComposerKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      askAssistant(event);
    }
  };

  return (
    <div className="mx-auto flex min-h-[calc(100vh-8rem)] w-full max-w-4xl flex-col">
      <header className="flex items-center justify-between border-b border-gray-200 px-1 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600 text-white">
            <FiHelpCircle aria-hidden="true" />
          </div>
          <div>
            <h1 className="font-semibold text-gray-950">{t('AI Help')}</h1>
            <p className="text-sm text-gray-500">{t('Ask about any page or workflow')}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={startNewChat}
          className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
          title={t('Clear conversation')}
          aria-label={t('Clear conversation')}
        >
          <FiRefreshCw aria-hidden="true" />
        </button>
      </header>

      <div className="flex-1 px-1 py-6">
        {messages.length === 0 ? (
          <div className="mx-auto flex min-h-[52vh] max-w-2xl flex-col items-center justify-center text-center">
            <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-lg bg-indigo-50 text-2xl text-indigo-700">
              <FiHelpCircle aria-hidden="true" />
            </div>
            <h2 className="text-2xl font-semibold text-gray-950">{t('How can I help?')}</h2>
            <p className="mt-2 max-w-xl text-gray-600">
              {t('Ask where to click, how a workflow works, or what a result means.')}
            </p>
            <div className="mt-7 grid w-full gap-2 sm:grid-cols-3">
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={(event) => askAssistant(event, suggestion)}
                  className="min-h-[76px] rounded-lg border border-gray-200 bg-white p-3 text-left text-sm font-medium leading-5 text-gray-700 hover:border-indigo-300 hover:bg-indigo-50"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="mx-auto max-w-3xl space-y-7">
            {messages.map((message) => (
              <div
                key={message.id}
                className={message.role === 'user' ? 'flex justify-end' : 'flex items-start gap-3'}
              >
                {message.role === 'assistant' && (
                  <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-sm text-white">
                    <FiHelpCircle aria-hidden="true" />
                  </div>
                )}
                <div
                  className={
                    message.role === 'user'
                      ? 'max-w-[82%] rounded-lg bg-gray-100 px-4 py-3 text-gray-900'
                      : `min-w-0 flex-1 leading-7 ${message.status === 'error' ? 'text-red-700' : 'text-gray-800'}`
                  }
                >
                  <div data-i18n-skip className="whitespace-pre-wrap">{message.content}</div>
                  {message.links?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {message.links.map((link) => (
                        <Link
                          key={`${message.id}-${link.path}`}
                          to={link.path}
                          className="inline-flex items-center rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100"
                        >
                          {link.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {asking && (
              <div className="flex items-center gap-3 text-gray-500">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600 text-sm text-white">
                  <FiHelpCircle aria-hidden="true" />
                </div>
                <div className="flex gap-1" aria-label={t('Thinking')}>
                  <span className="h-2 w-2 animate-pulse rounded-full bg-gray-400" />
                  <span className="h-2 w-2 animate-pulse rounded-full bg-gray-400 [animation-delay:150ms]" />
                  <span className="h-2 w-2 animate-pulse rounded-full bg-gray-400 [animation-delay:300ms]" />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="sticky bottom-0 bg-gray-50 pb-2 pt-3">
        <form
          onSubmit={askAssistant}
          className="mx-auto flex max-w-3xl items-end gap-2 rounded-lg border border-gray-300 bg-white p-2 shadow-sm focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100"
        >
          <textarea
            ref={textareaRef}
            value={question}
            onChange={(event) => setQuestion(event.target.value)}
            onKeyDown={handleComposerKeyDown}
            rows={1}
            maxLength={3000}
            disabled={asking}
            className="max-h-40 min-h-[44px] flex-1 resize-none border-0 bg-transparent px-3 py-2.5 text-gray-900 outline-none disabled:text-gray-500"
            placeholder={t('Message AI Help')}
            aria-label={t('Message AI Help')}
          />
          <button
            type="submit"
            disabled={asking || !question.trim()}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-gray-300"
            title={t('Send')}
            aria-label={t('Send')}
          >
            <FiArrowUp aria-hidden="true" />
          </button>
        </form>
        <p className="mt-2 text-center text-xs text-gray-500">
          {t('AI Help answers from the platform guide. Check important details before acting.')}
        </p>
      </div>
    </div>
  );
};

export default HelpManualPage;
